import { placeholderImage } from '../../../shared/mb-constants'
import { ChangeDetectorRef, Component, OnDestroy } from '@angular/core'
import { Observable, Subscription } from 'rxjs'
import { EventService, isNotNullable, Product } from '@spartacus/core'
import { BREAKPOINT, BreakpointService, CurrentProductService } from '@spartacus/storefront'
import { filter, map, shareReplay, take, tap } from 'rxjs/operators'
import { PdpGalleryDialogComponent } from '../../../shared/components/pdp-gallery-dialog/pdp-gallery-dialog.component'
import { ImageGroup } from '@spartacus/core/src/model/image.model'
import { MovingImageService } from '../../../cms/services/moving-image/moving-image.service'
import { MbConstants } from '../../../shared/mb-constants'
import { PdpGalleryService } from '../../../shared/services/pdp-gallery.service'
import { GalleryImageData } from '../../../models'
import { MbModalService } from '../../../shared/services/modal.service'
import { UcService } from '../../../cms/enums/uc-service'
import { GotVideoByIdEvent } from '../../../cms/components/shared/events/got-video-by-id-event'
import { MiVideo } from '../../../cms/services/moving-image/moving-image'

@Component({
  selector: 'mb-pdp-gallery',
  templateUrl: './pdp-gallery.component.html',
  styleUrls: ['./pdp-gallery.component.scss']
})
export class PdpGalleryComponent implements OnDestroy {

  placeholderImage = placeholderImage
  activeImage: GalleryImageData | undefined
  previousActiveImage: GalleryImageData | undefined
  product: Product | undefined
  thumbnails: GalleryImageData[] = []
  videoThumbnailSmall: GalleryImageData[] = []
  subscription: Subscription = new Subscription()
  constants = MbConstants
  product$ = this.currentProductService.getProduct().pipe(
    filter(isNotNullable),
    tap(product => {
      if (product?.originalCode !== this.product?.originalCode) {
        this.product = product
        const portfolioImageData: GalleryImageData = { image: (product?.images?.PRIMARY as ImageGroup)?.['product']?.url }
        const applicationDataImagesData = this.product.applicationImages || []
        this.activeImage = applicationDataImagesData.length > 0 && applicationDataImagesData[0] || portfolioImageData
        this.product.applicationImages?.filter(image => !!image.videoId).forEach(image => {
          this.subscription.add(this.miService.getVideoById(image?.videoId ?? '').pipe(take(1))
            .subscribe(
            video => {
              this.thumbnails?.push({
                image: video?.stills?.find((still) => still.quality === this.constants.THUMBNAIL_FORMAT)?.url ?? '/assets/images/video_thumbnail.jpg',
                videoId: image.videoId,
                legend: image.legend
              })
              this.videoThumbnailSmall?.push({
                image: video?.stills?.find((still) => still.quality === this.constants.HIGH_RESOLUTION_FORMAT)?.url ?? '',
                videoId: image.videoId,
                legend: image.legend
              })
              this.cdRef.detectChanges()
            }
          ))
        })
      }
    }),
    shareReplay(1)
  )
  productGallery$ = this.product$.pipe(
    map(product => {
      const portfolioImageData = { image: (product?.images?.PRIMARY as ImageGroup)?.['product']?.url }
      const applicationDataImagesData = product.applicationImages || []
      return portfolioImageData?.image ? applicationDataImagesData.concat(portfolioImageData) : applicationDataImagesData
    }),
    shareReplay(1)
  )
  productName$ = this.product$.pipe(
    map(product => product?.name),
    shareReplay(1)
  )
  hasApplicationData$ = this.product$.pipe(
    map(product => (product.applicationImages?.length || 0) > 1),
    shareReplay(1)
  )
  isImageClickable$ = this.product$.pipe(
    map(product => (product.applicationImages?.length || 0) > 1 && product.applicationImages?.[0].legend),
    shareReplay(1)
  )

  constructor (protected currentProductService: CurrentProductService,
               protected modalService: MbModalService,
               protected miService: MovingImageService,
               protected breakpointService: BreakpointService,
               protected pdpGalleryService: PdpGalleryService,
               protected cdRef: ChangeDetectorRef,
               protected eventService: EventService) {
    this.subscription.add(this.eventService.get(GotVideoByIdEvent)
      .subscribe(event => {
        const video: MiVideo = event?.video
        const videoId: string = event?.video?.id

        this.thumbnails?.map(thumbnail =>
          thumbnail?.videoId === videoId ? thumbnail.image = video?.stills?.find((still) => still.quality === this.constants.THUMBNAIL_FORMAT)?.url ?? '' : undefined)

        this.videoThumbnailSmall?.map(thumbnail =>
          thumbnail?.videoId === videoId ? thumbnail.image = video?.stills?.find((still) => still.quality === this.constants.HIGH_RESOLUTION_FORMAT)?.url ?? '' : undefined)

        this.cdRef.detectChanges()
      }))
  }

  onGalleryClickedHandler (imageData: GalleryImageData) {
    this.previousActiveImage = this.activeImage
    this.activeImage = imageData

    if (this.previousActiveImage?.videoId) {
      this.miService.removeBlockedContentReplacerFromDOM(this.previousActiveImage?.videoId, 'use-to-remove-blocker')
    }
  }

  onThumbnailImageClickHandler () {
    if (this.product?.applicationImages?.length &&
      this.product?.applicationImages?.length > 1 &&
      (this.product?.applicationImages?.some(image => image?.legend) ||
        this.product?.applicationImages?.some(image => image?.videoId))) {
      const modalRef = this.modalService.open(PdpGalleryDialogComponent, {
        centered: true,
        scrollable: true,
        size: 'xl',
        modalDialogClass: 'mb-ng-modal',
        windowClass: 'pdp-gallery-ng-modal'
      })
      const portfolioImageData = { image: (this.product?.images?.PRIMARY as ImageGroup)?.['product']?.url }
      modalRef.componentInstance.activeImage = this.activeImage
      modalRef.componentInstance.gallery =
        portfolioImageData?.image ? this.product?.applicationImages?.concat(portfolioImageData) : this.product?.applicationImages
      modalRef.componentInstance.hasApplicationData = this.product?.applicationImages?.length > 1
      modalRef.componentInstance.productName = this.product?.name
      modalRef.componentInstance.videoThumbnails = this.thumbnails
    }
  }

  getThumbnail (videoId: string): GalleryImageData | undefined {
    return this.pdpGalleryService.getThumbnail(videoId, this.thumbnails)
  }

  isScreenForSmallDevices (): Observable<boolean> {
    return this.breakpointService?.isDown(BREAKPOINT.md)
  }

  getThumbnailForSmallDevices (videoId: string): GalleryImageData | undefined {
    return this.pdpGalleryService.getThumbnail(videoId, this.videoThumbnailSmall)
  }

  ngOnDestroy (): void {
    this.subscription.unsubscribe()
  }

  readonly ucService = UcService
}
